import { __rest } from "tslib";
import omitBy from 'lodash-es/omitBy';
import DefinitionManager from './DefinitionManager';
import defineLazyGetter from './utils/defineLazyGetter';
import { isNullish } from './utils/isNonNullable';
import mergeArray from './utils/mergeArray';
import mergeDataSchema from './utils/mergeDataSchema';
export const PatchedDataSchemaMark = Symbol('isPatchedDataSchema');
export function isPatchedDataSchema(x) {
    return typeof x === 'object' && x !== null && x[PatchedDataSchemaMark];
}
/**
 * 输入一个 DataSchema，返回一个经过 definition 补全的 DataSchema
 *
 * 注意，返回的那个玩意儿是完全只读的，不要试图修改他
 */
export default function getPatchedDataSchema(dataSchema) {
    const definitionManager = new DefinitionManager(dataSchema);
    const schemaToPatchedDataSchema = new WeakMap();
    const patchDataSchema = (schema, { isObjectField = false } = {}) => {
        var _a, _b, _c, _d;
        {
            const prevAns = schemaToPatchedDataSchema.get(schema);
            if (prevAns)
                return prevAns;
            if (isPatchedDataSchema(schema)) {
                schemaToPatchedDataSchema.set(schema, schema);
                // 已经是 patched 的了，就不需要再折腾了
                return schema;
            }
        }
        let ans = Object.assign({}, schema);
        schemaToPatchedDataSchema.set(schema, ans);
        // 做一些基本的补全工作
        (_a = ans.type) !== null && _a !== void 0 ? _a : (ans.type = 'any');
        if (isObjectField)
            (_b = ans.required) !== null && _b !== void 0 ? _b : (ans.required = true);
        if (ans.type === 'object')
            (_c = ans.fields) !== null && _c !== void 0 ? _c : (ans.fields = []);
        if (ans.type === 'array')
            (_d = ans.items) !== null && _d !== void 0 ? _d : (ans.items = { type: 'any' });
        // 然后是如果有定义 ref，就做 patch
        if (ans.$ref) {
            const oldRef = ans.$ref;
            const refParent = definitionManager.getDefinition(oldRef);
            if (refParent) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { id } = refParent, refParentWithoutId = __rest(refParent, ["id"]);
                ans = mergeDataSchema(ans, refParentWithoutId);
                ans.$refBeforePatched = oldRef;
                const currentSchemaHasChanges = false
                    || (schema.type || refParent.type) !== refParent.type
                    || (Array.isArray(schema.fields) && schema.fields.length > 0)
                    || (!!schema.items)
                    || (!!schema.additionalFields)
                    || false;
                if (!currentSchemaHasChanges) {
                    ans.$isIdenticalToRef = schemaToPatchedDataSchema.get(refParent);
                }
            }
            delete ans.$ref;
        }
        // 然后是对于嵌套了子 DataSchema 的情况，需要定义 getter 们
        ['fields'].forEach((k) => {
            const rawValue = ans[k];
            if (!Array.isArray(rawValue) || rawValue.length === 0)
                return;
            // 在第一次访问property时，自动递归 patch
            // 这里要留意，要过滤空值，而且要去重
            defineLazyGetter(ans, k, () => mergeArray(rawValue.filter(Boolean).map(x => patchDataSchema(x, {
                isObjectField: true,
            })), null, 'id'));
        });
        ['items', 'additionalFields'].forEach((k) => {
            const rawValue = ans[k];
            if (!rawValue)
                return;
            defineLazyGetter(ans, k, () => patchDataSchema(rawValue));
        });
        {
            const rawMethods = ans.methods;
            if (rawMethods) {
                defineLazyGetter(ans, 'methods', () => mergeArray(rawMethods, null, 'id').map(method => omitBy(
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                Object.assign(Object.assign({}, method), { arguments: (method.arguments || []).map(it => patchDataSchema(it)), returnValue: method.returnValue && patchDataSchema(method.returnValue) }), isNullish)));
            }
        }
        ans[PatchedDataSchemaMark] = true;
        return ans;
    };
    return patchDataSchema(dataSchema);
}
